import {useState} from "react";
import {Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Button} from "@/components/ui/button.tsx";
import {PlusCircleIcon, TrashIcon} from "@heroicons/react/24/outline";
import {toast} from "sonner";
import {useCreateUserMutation, useGetDynamicFieldsQuery} from "../api/userDirectoryApi";
import {DynamicFieldsFormSection} from "./DynamicFieldsFormSection";
import {CreateUserPayload, LearningUserDetail} from "../types";

export interface CreateUserModalCreatedInfo {
    firstName: string;
    lastName: string;
    phone: string;
}

interface CreateUserModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    // נקרא לאחר יצירה מוצלחת; מאפשר לצרכן (למשל מסך שיוך הצ'יפ) לבצע פעולת המשך (כמו יצירת TicketUser)
    // בלי לצמד את המודול הגלובלי הזה ללוגיקה ספציפית.
    onCreated: (user: LearningUserDetail, formValues: CreateUserModalCreatedInfo) => void;
    initialSearchTerm?: string;
}

// מודאל יצירת learning.User גנרי — לשימוש חוזר בכל מסך בפרויקט.
export function CreateUserModal({open, onOpenChange, onCreated, initialSearchTerm}: CreateUserModalProps) {
    const {data: dynamicFields = []} = useGetDynamicFieldsQuery();
    const [createUser, {isLoading}] = useCreateUserMutation();

    const initialIsDigits = Boolean(initialSearchTerm && /^[\d\-\s]+$/.test(initialSearchTerm));

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [tz, setTz] = useState(initialIsDigits ? initialSearchTerm ?? "" : "");
    const [phones, setPhones] = useState<string[]>([""]);
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [className, setClassName] = useState("");
    const [group, setGroup] = useState("");
    const [dynamicValues, setDynamicValues] = useState<Record<string, string | boolean>>({});

    function resetForm() {
        setFirstName("");
        setLastName("");
        setTz("");
        setPhones([""]);
        setAddress("");
        setCity("");
        setClassName("");
        setGroup("");
        setDynamicValues({});
    }

    function updatePhone(index: number, value: string) {
        setPhones((prev) => prev.map((p, i) => (i === index ? value : p)));
    }

    function addPhone() {
        setPhones((prev) => [...prev, ""]);
    }

    function removePhone(index: number) {
        setPhones((prev) => prev.filter((_, i) => i !== index));
    }

    async function handleSubmit() {
        if (!firstName.trim() || !lastName.trim()) {
            toast.error("יש להזין שם פרטי ושם משפחה");
            return;
        }
        const validPhones = phones.map((p) => p.trim()).filter(Boolean);
        if (validPhones.length === 0) {
            toast.error("יש להזין לפחות מספר טלפון אחד");
            return;
        }

        const payload: CreateUserPayload = {
            name: `${firstName.trim()} ${lastName.trim()}`,
            tz: tz.trim() || undefined,
            address: address.trim() || undefined,
            city: city.trim() || undefined,
            class_name: className.trim() || undefined,
            group: group.trim() || undefined,
            phones: validPhones,
            settings: dynamicValues,
        };

        try {
            const user = await createUser(payload).unwrap();
            toast.success("המשתמש נוצר בהצלחה");
            const info: CreateUserModalCreatedInfo = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: validPhones[0],
            };
            resetForm();
            onOpenChange(false);
            onCreated(user, info);
        } catch (err: unknown) {
            const msg = (err as {data?: {msg?: string}})?.data?.msg || "שגיאה ביצירת המשתמש";
            toast.error(msg);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>יצירת משתמש חדש</DialogTitle>
                </DialogHeader>
                <DialogBody className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-base font-medium">שם פרטי *</Label>
                            <Input
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="text-base h-11"
                                disabled={isLoading}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-base font-medium">שם משפחה *</Label>
                            <Input
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="text-base h-11"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-base font-medium">טלפונים *</Label>
                        {phones.map((phone, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <Input
                                    value={phone}
                                    onChange={(e) => updatePhone(index, e.target.value)}
                                    className="text-base h-11"
                                    disabled={isLoading}
                                    placeholder="מספר טלפון"
                                />
                                <div className="flex items-center gap-1 shrink-0">
                                    <Button
                                        type="button"
                                        size="icon-sm"
                                        variant="ghost"
                                        className="rounded-full text-gray-400"
                                        onClick={() => removePhone(index)}
                                        disabled={isLoading || phones.length === 1}
                                    >
                                        <TrashIcon />
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={addPhone}
                                        size="icon-sm"
                                        variant="ghost"
                                        className={`rounded-full ${index !== phones.length - 1 ? 'opacity-0 pointer-events-none' : ''}`}
                                        disabled={isLoading}
                                    >
                                        <PlusCircleIcon className="!size-6" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="space-y-2">
                        <Label className="text-base font-medium">ת.ז.</Label>
                        <Input
                            value={tz}
                            onChange={(e) => setTz(e.target.value)}
                            className="text-base h-11"
                            disabled={isLoading}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-base font-medium">כתובת</Label>
                            <Input
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                className="text-base h-11"
                                disabled={isLoading}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-base font-medium">עיר</Label>
                            <Input
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                className="text-base h-11"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-base font-medium">כיתה</Label>
                            <Input
                                value={className}
                                onChange={(e) => setClassName(e.target.value)}
                                className="text-base h-11"
                                disabled={isLoading}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-base font-medium">קבוצה</Label>
                            <Input
                                value={group}
                                onChange={(e) => setGroup(e.target.value)}
                                className="text-base h-11"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <DynamicFieldsFormSection
                        fields={dynamicFields}
                        values={dynamicValues}
                        onChange={(name, value) => setDynamicValues((prev) => ({...prev, [name]: value}))}
                        disabled={isLoading}
                    />
                </DialogBody>
                <DialogFooter>
                    <Button onClick={handleSubmit} disabled={isLoading} className="text-base h-11">
                        {isLoading ? "יוצר..." : "צור משתמש"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
